import React, { useState, useCallback, useEffect } from 'react';
import googleSheets from '../../services/googleSheets';
import { Customer } from '../../types';
import { ValidationUtils } from '../../utils/validation';
import MotivationalLoader from '../MotivationalLoader';
import RichTextEditor from '../AddCustomerForm/RichTextEditor';
import './UpdateCustomerModal.css';

interface UpdateCustomerModalProps {
  customer: Customer;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedCustomer: Customer) => void;
}

interface FormData {
  email: string;
  companyName: string;
  segment: Customer['segment'];
  signupDate: string;
  companyGoal: string;
}

interface FormErrors {
  email?: string;
  companyName?: string;
  segment?: string;
  signupDate?: string;
  companyGoal?: string;
}

const UpdateCustomerModal: React.FC<UpdateCustomerModalProps> = ({
  customer,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    email: customer.email,
    companyName: customer.companyName,
    segment: customer.segment,
    signupDate: customer.signupDate,
    companyGoal: customer.companyGoal || '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when customer changes or modal opens
  useEffect(() => {
    if (isOpen && customer) {
      setFormData({
        email: customer.email,
        companyName: customer.companyName,
        segment: customer.segment,
        signupDate: customer.signupDate,
        companyGoal: customer.companyGoal || '',
      });
      setErrors({});
      setError(null);
      setSuccess(false);
    }
  }, [customer, isOpen]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!ValidationUtils.validateEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Company name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    // Signup date validation
    if (!formData.signupDate) {
      newErrors.signupDate = 'Signup date is required';
    } else {
      const signupDate = new Date(formData.signupDate);
      const now = new Date();
      if (signupDate > now) {
        newErrors.signupDate = 'Signup date cannot be in the future';
      }
    }

    // Company goal validation (optional but if provided, should have minimum length)
    if (formData.companyGoal.trim()) {
      // Strip HTML tags for length validation
      const textContent = formData.companyGoal.replace(/<[^>]*>/g, '').trim();
      if (textContent.length < 10) {
        newErrors.companyGoal = 'Company goal should be at least 10 characters if provided';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if email has changed and if the new email already exists
      if (formData.email.trim().toLowerCase() !== customer.email.toLowerCase()) {
        const existingCustomers = await googleSheets.getAllCustomers();
        const emailExists = existingCustomers.some(c => 
          c.id !== customer.id && c.email.toLowerCase() === formData.email.trim().toLowerCase()
        );
        
        if (emailExists) {
          setErrors({ email: 'A customer with this email already exists' });
          setLoading(false);
          return;
        }
      }

      // Create updated customer object with only changed fields
      const updatedFields: Partial<Customer> = {};
      
      if (formData.email.trim() !== customer.email) {
        updatedFields.email = formData.email.trim();
      }
      if (formData.companyName.trim() !== customer.companyName) {
        updatedFields.companyName = ValidationUtils.sanitizeInput(formData.companyName.trim());
      }
      if (formData.segment !== customer.segment) {
        updatedFields.segment = formData.segment;
      }
      if (formData.signupDate !== customer.signupDate) {
        updatedFields.signupDate = formData.signupDate;
      }
      if (formData.companyGoal.trim() !== (customer.companyGoal || '')) {
        updatedFields.companyGoal = formData.companyGoal.trim();
      }

      // Only update if there are changes
      if (Object.keys(updatedFields).length === 0) {
        setError('No changes detected');
        setLoading(false);
        return;
      }

      // Update customer in Google Sheets
      await googleSheets.updateCustomer(customer.id, updatedFields);

      // Create the updated customer object for callback
      const updatedCustomer: Customer = {
        ...customer,
        ...updatedFields,
      };

      setSuccess(true);
      onUpdate(updatedCustomer);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  }, [formData, customer, onUpdate, onClose, validateForm]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-customer-title"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="update-customer-title">Edit Customer</h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {success ? (
          <div className="modal-success">
            <div className="success-message">
              <h3>✅ Customer Updated Successfully!</h3>
              <p>The customer information has been updated.</p>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="modal-loading">
                <MotivationalLoader type="general" size="md" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="update-customer-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="customerId">
                      Customer ID
                    </label>
                    <input
                      type="text"
                      id="customerId"
                      value={customer.id}
                      disabled
                      className="readonly-field"
                      title="Customer ID cannot be changed"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="customer@company.com"
                      className={errors.email ? 'error' : ''}
                      disabled={loading}
                    />
                    {errors.email && (
                      <span className="field-error">{errors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="companyName">
                      Company Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="Enter company name"
                      className={errors.companyName ? 'error' : ''}
                      disabled={loading}
                    />
                    {errors.companyName && (
                      <span className="field-error">{errors.companyName}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="segment">
                      Segment <span className="required">*</span>
                    </label>
                    <select
                      id="segment"
                      value={formData.segment}
                      onChange={(e) => handleInputChange('segment', e.target.value as Customer['segment'])}
                      className={errors.segment ? 'error' : ''}
                      disabled={loading}
                    >
                      <option value="small_business">Small Business</option>
                      <option value="enterprise">Enterprise</option>
                      <option value="e-commerce">E-commerce</option>
                    </select>
                    {errors.segment && (
                      <span className="field-error">{errors.segment}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signupDate">
                      Signup Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="signupDate"
                      value={formData.signupDate}
                      onChange={(e) => handleInputChange('signupDate', e.target.value)}
                      className={errors.signupDate ? 'error' : ''}
                      disabled={loading}
                      max={new Date().toISOString().split('T')[0]}
                    />
                    {errors.signupDate && (
                      <span className="field-error">{errors.signupDate}</span>
                    )}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="companyGoal">
                    Company Goal
                  </label>
                  <RichTextEditor
                    value={formData.companyGoal}
                    onChange={(value) => handleInputChange('companyGoal', value)}
                    placeholder="Describe the company's goals and objectives..."
                    className={errors.companyGoal ? 'error' : ''}
                    readOnly={loading}
                  />
                  {errors.companyGoal && (
                    <span className="field-error">{errors.companyGoal}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Customer'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UpdateCustomerModal;